import { BaseApi } from './base.api';
import { handleResponse, handleData } from '../_services/response.handler';
import { wrapAsyncWithCache, shallowCloneJsonLike } from '../utils/asyncResultCache';
import { unwrapServiceContent } from '../utils/defect/unwrapServiceContent';
import { normalizePanelDefectForApi } from '../utils/defect/panelDefectShape';

//대부분의 결함 관련 api들이 여기에 있음
export const moduleDefectApi = {
  // 결함 목록 조회
  getDefectsByThermalImageId: async (id, aiInclude = false) => {
    try {
      const response = await BaseApi.get(`/module-defects/search/solarThermalImageId/${id}?aiInclude=${aiInclude}`);
      return handleData(response);
    } catch (error) {
      console.error('[MODULE DEFECT API] 결함 목록 조회 실패:', error);
      throw error;
    }
  },

  // 결함 상세 조회
  getDefectById: async (id) => {
    try {
      const response = await BaseApi.get(`/moduleDefects/${id}`);
      return handleData(response);
    } catch (error) {
      console.error('[MODULE DEFECT API] 결함 상세 조회 실패:', error);
      throw error;
    }
  },

  // 결함 생성
  createDefect: async (defectData) => {
    try {
      console.log('[MODULE DEFECT API] 결함 생성 시작');
      console.log('[MODULE DEFECT API] 요청 데이터:', defectData);

      // 인증 토큰 확인
      const authToken = localStorage.getItem('auth-token');
      console.log('[MODULE DEFECT API] 인증 토큰:', authToken ? '존재함' : '없음');

      const response = await BaseApi.post('/moduleDefects', defectData);
      console.log('[MODULE DEFECT API] 결함 생성 성공:', response);
      return handleData(response);
    } catch (error) {
      console.error('[MODULE DEFECT API] 결함 생성 실패:', error);
      console.error('[MODULE DEFECT API] 에러 상세:', error.response?.data);
      throw error;
    }
  },

  // 결함 수정
  updateDefect: async (id, defectData) => {
    try {
      console.log('[MODULE DEFECT API] 결함 수정 시작');
      console.log('[MODULE DEFECT API] 결함 ID:', id);
      console.log('[MODULE DEFECT API] 요청 데이터 전체:', JSON.stringify(defectData, null, 2));
      console.log('[MODULE DEFECT API] defectStatus 값:', defectData.defectStatus);
      console.log('[MODULE DEFECT API] defectDesc 값:', defectData.defectDesc);
      console.log('[MODULE DEFECT API] defectCode 값:', defectData.defectCode);
      console.log('[MODULE DEFECT API] detectWay 값:', defectData.detectWay);
      console.log('[MODULE DEFECT API] mlDefectConfirmed 값:', defectData.mlDefectConfirmed);
      console.log('[MODULE DEFECT API] 온도 정보:', {
        maxHeat: defectData.maxHeat,
        minHeat: defectData.minHeat,
        averageHeat: defectData.averageHeat
      });

      // 인증 토큰 확인
      const authToken = localStorage.getItem('auth-token');
      console.log('[MODULE DEFECT API] 인증 토큰:', authToken ? '존재함' : '없음');

      const response = await BaseApi.put(`/moduleDefects/${id}`, defectData);
      console.log('[MODULE DEFECT API] 결함 수정 성공:', response);
      return handleData(response);
    } catch (error) {
      console.error('[MODULE DEFECT API] 결함 수정 실패:', error);
      console.error('[MODULE DEFECT API] 에러 상세:', error.response?.data);
      throw error;
    }
  },

  // 결함 삭제
  deleteDefect: async (id) => {
    try {
      const response = await BaseApi.delete(`/moduleDefects/${id}`);
      return handleResponse(response);
    } catch (error) {
      console.error('[MODULE DEFECT API] 결함 삭제 실패:', error);
      throw error;
    }
  },

  /**
   * 열화상 이미지 픽셀 사각형 기준 인근 태양광 모듈 조회
   * POST /module-defects/thermal-image/{thermalImageId}/near-modules
   */
  findSolarModulesNearDefect: async (thermalImageId, panelDefect) => {
    const shape = normalizePanelDefectForApi(panelDefect);
    if (thermalImageId == null || thermalImageId === '' || !shape) {
      return [];
    }

    try {
      const response = await BaseApi.post(
        `/module-defects/thermal-image/${thermalImageId}/near-modules`,
        shape
      );
      const payload = unwrapServiceContent(handleData(response));
      return Array.isArray(payload) ? payload : [];
    } catch (error) {
      console.error('[MODULE DEFECT API] 인근 모듈 조회 실패:', error);
      throw error;
    }
  },

  // 결함 위치 조정
  adjustDefectLocation: async (defectId, data) => {
    try {
      if (!defectId || defectId === null || defectId === undefined) {
        console.warn('[MODULE DEFECT API] adjustDefectLocation: defectId가 유효하지 않습니다:', defectId);
        throw new Error('Invalid defectId');
      }

      const response = await BaseApi.patch(`/module-defects/${defectId}/coords`, data);
      return handleResponse(response);
    } catch (error) {
      console.error('[MODULE DEFECT API] 결함 위치 조정 실패:', error);
      throw error;
    }
  },

  // 하위 결함 타입 조회 (findByParent_Seq — wrapAsyncWithCache 로 parent별 세션 캐시)
  getChildrenDefectTypes: wrapAsyncWithCache(
    async (parentId) => {
      try {
        console.log('[MODULE DEFECT API] 하위 결함 타입 조회 시작:', parentId);
        const response = await BaseApi.get(`/defectTypes/search/findByParent_Seq?parent=${parentId}`);
        console.log('[MODULE DEFECT API] 하위 결함 타입 조회 완료:', response);

        const data = handleData(response);

        if (data && data._embedded && data._embedded.defectTypes) {
          return data._embedded.defectTypes;
        }

        return data;
      } catch (error) {
        console.error('[MODULE DEFECT API] 하위 결함 타입 조회 실패:', error);
        throw error;
      }
    },
    {
      cacheKey: (parentId) =>
        parentId != null && parentId !== '' ? String(parentId) : null,
      maxEntries: 64,
      cloneResult: shallowCloneJsonLike
    }
  ),

  /** 결함 타입 단건 (지도 팝업 등). origin/main 이후 로컬에서 추가된 호출부 호환용. */
  getDefectTypeById: async (defectTypeId) => {
    if (defectTypeId == null || defectTypeId === '') {
      return null;
    }
    try {
      const response = await BaseApi.get(`/defectTypes/${defectTypeId}`);
      return handleData(response);
    } catch (error) {
      console.error('[MODULE DEFECT API] 결함 타입 단건 조회 실패:', error);
      return null;
    }
  },

  // 결함 타입 description 조회
  getDefectTypeDescription: async (defectTypeId) => {
    try {
      console.log('[MODULE DEFECT API] 결함 타입 description 조회 시작:', defectTypeId);
      const response = await BaseApi.get(`/defectTypes/${defectTypeId}`);
      console.log('[MODULE DEFECT API] 결함 타입 description 조회 완료:', response);

      const data = handleData(response);
      return data?.description || '';
    } catch (error) {
      console.error('[MODULE DEFECT API] 결함 타입 description 조회 실패:', error);
      throw error;
    }
  },

  // AI가 탐지한 미확인 결함 목록 조회
  getAIDetectedDefects: async (reportId) => {
    try {
      console.log('[MODULE DEFECT API] AI 탐지 미확인 결함 조회 시작:', reportId);
      const response = await BaseApi.get(`/module-defects/report/${reportId}/ai-detects`);
      console.log('[MODULE DEFECT API] AI 탐지 미확인 결함 조회 완료:', response);
      return handleData(response);
    } catch (error) {
      console.error('[MODULE DEFECT API] AI 탐지 미확인 결함 조회 실패:', error);
      throw error;
    }
  },

  // 결함 일괄 삭제
  deleteModuleDefects: async (defectIds) => {
    try {
      console.log('[MODULE DEFECT API] 결함 일괄 삭제 시작:', defectIds);

      // 배열을 쿼리 파라미터로 변환
      const params = new URLSearchParams();
      defectIds.forEach(id => params.append('defect_id', id));

      const response = await BaseApi.delete(`/module-defects?${params.toString()}`);
      console.log('[MODULE DEFECT API] 결함 일괄 삭제 완료:', response);
      return handleResponse(response);
    } catch (error) {
      console.error('[MODULE DEFECT API] 결함 일괄 삭제 실패:', error);
      throw error;
    }
  },

  /**
   * 보고서 orphan 결함에 solar_module_seq 재할당
   * POST /module-defects/report/{reportId}/relink-modules
   */
  relinkOrphanModules: async reportId => {
    try {
      const response = await BaseApi.post(`/module-defects/report/${reportId}/relink-modules`);
      const payload = unwrapServiceContent(handleData(response));
      console.log('[MODULE DEFECT API] orphan 모듈 재연결:', payload);
      return payload;
    } catch (error) {
      console.error('[MODULE DEFECT API] orphan 모듈 재연결 실패:', error);
      throw error;
    }
  }
};
